import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Header from "../components/Header";
import Banner from "../components/Banner";
import PrimeLayout from "../components/PrimeLayout";
import StyledButton from "../components/elements/StyledButton";
import StyledSelect from "../components/elements/StyledSelect";
import StyledTable from "../components/StyledTable";
import ModelDialog from "../components/ModelDialog";
import { Grid } from "@mui/material";
import { MdLibraryAdd } from "react-icons/md";
import { IoMdListBox } from "react-icons/io";
import { modelHeaders } from "../constants/Data";
import { saveAs } from "file-saver";
import ModelMatrix from "../components/ModelMatrix";
import Footer from "../components/Footer";

import {
  addDeleteVersion,
  addTemplate,
  deleteTemplate,
  getAllTemplates,
  getModelMatrixData,
  patchTemplate,
} from "../services/api/models.api";
import moment from "moment";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Pagination from "../components/elements/Pagination";
import DialogWrapper from "../components/DialogWrapper";
import DeleteConfirmation from "../components/DeleteConfirmation";
import VersionPicker from "../components/VersionPicker";
import { VersionWrapper } from "../components/styled/Models.styled";
import { downloadFile } from "../services/api/fileUpload";

const SubTitleWrapper = styled.div`
  margin-left: 20px;
`;

export default function Models() {
  const [model, setModel] = useState("init");
  const [matrixModelIsOpen, setMatrixModelIsOpen] = useState(false);
  const [order, setOrder] = useState({ id: "name", sort: "asc" });
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editModelData, setEditModelData] = useState([]);
  const [page, setPage] = useState(1);
  const [reRender, setReRender] = useState(false);
  const [modelArray, setModelArray] = useState([]);
  const [modelRawData, setModelRawData] = useState([]);
  const [dataForEdit, setDataForEdit] = useState({});
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [allModels, setAllModels] = useState({});
  const [modelToDelete, setModelToDelete] = useState({});
  const [openDelete, setOpenDelete] = useState(false);
  const [matrixDataRows, setMatrixDataRows] = useState([]);
  const [matrixVersionData, setMatrixVersionData] = useState(null);
  const [uniqueVersions, setUniqueVersions] = useState([]);
  const [openVersion, setOpenVersion] = useState(false);
  const [versionsToBeSaved, setVersionsToBeSaved] = useState([]);
  
  useEffect(() => {
    if (user) {
      if (user?.user_role !== "admin" && user?.user_role !== "super_admin") {
        toast.error("You do not have access to this page");
        return navigate("/new-upload");
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const getAllModels = async () => {
      try {
        setLoading(true);
        const modelPaginatedData = await getAllTemplates(
          10,
          order.id,
          order.sort,
          model === "All modules" ? undefined : model,
          page
        );
        const rowData = modelPaginatedData.docs.map((temp) => {
          let row = {};
          row.id = temp._id;
          row.model = temp.name;
          row.template = getTemplateNameFromUrl(temp.url);
          row.date = moment(temp.date).format("DD-MM-YYYY, HH:mm");
          row.created_by = temp.created_by || "";
          row.url = temp.url;
          return row;
        });
        setModelRawData([...modelPaginatedData.docs]);
        const listModels = modelPaginatedData.models.map((temp) => {
          return temp.name;
        }).sort();
        setAllModels(modelPaginatedData);
        setModelArray(["All modules", ...listModels]);
        setEditModelData([...rowData]);
        setLoading(false);
      } catch (err) {
        if (err.message === "No active account") {
          toast.error("Session Expired, Please Log back", {
            theme: "colored",
          });
          return navigate("/");
        }
        toast.error(err.message, { theme: "colored" });
      }
    };

    getAllModels();
    // eslint-disable-next-line
  }, [reRender, model, order, page]);

  const getTemplateNameFromUrl = (url) => {
    const arrayAfterSplit = url.split("-");
    if (arrayAfterSplit.length > 0) {
      return arrayAfterSplit[arrayAfterSplit.length - 1];
    }
    return url;
  };

  const addNewTemplate = async (e) => {
    try {
      setLoading(true);
      setMatrixModelIsOpen(false);
      e.date = Date.now();
      let result = await addTemplate(e);
      if (result.error) {
        toast.error(result.error, {
          theme: "colored",
        });
      } else {
        toast.success("Module added successfully", { theme: "colored" });
      }
      setReRender(!reRender);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setReRender(!reRender);
      toast.error("Failed to add module", { theme: "colored" });
    }
  };

  const editTemplate = async (e) => {
    try {
      setLoading(true);
      const dataEdit = {
        name: e.name,
        url: e.url,
        countries: e.countries,
        users: e.usersObjects,
        headers: e.headers,
      };
      let result = await patchTemplate(dataForEdit._id, dataEdit);
      if (result.error) {
        toast.error(result.error, {
          theme: "colored",
        });
      } else {
        toast.success(`${e.name} module was successfully edited`, {
          theme: "colored",
        });
      }

      setDataForEdit({});
      setReRender(!reRender);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(`Failed to edit module`, { theme: "colored" });
    }
  };

  const handleEditModel = (e) => {
    const editData = modelRawData.findIndex((d) => d._id.toString() === e.id);
    setDataForEdit(modelRawData[editData]);
    setOpenEdit(true);
  };

  const handleAddClickModel = () => {
    setOpen(true);
  };

  const handleDeleteCheck = (e) => {
    setModelToDelete(e);
    setOpenDelete(true);
  };

  const cancelDelete = () => {
    setModelToDelete(null);
    setOpenDelete(false);
  };

  const handleDeleteModel = async () => {
    try {
      await deleteTemplate(modelToDelete.id);
      setReRender(!reRender);
      toast.success(`Module ${modelToDelete.model} deleted successfully`, {
        theme: "colored",
      });
      setOpenDelete(false);
      setModelToDelete(null);
    } catch (err) {
      setOpenDelete(false);
      setModelToDelete(null);
      setReRender(!reRender);
    }
  };

  const downloadModel = async (e) => {
    try {
      const downloadURL = await downloadFile({ name: encodeURI(e.data.url) });
      saveAs(downloadURL, "template.xlsx");
    } catch (err) {
      toast.error("Failed to download module", { theme: "colored" });
    }
  };

  const getPage = (p) => {
    setPage(p);
  };

  const onClickModelMatrix = async () => {
    try {
      const matrixData = await getModelMatrixData();
      let formattedMatrixData = matrixData.map(({ file, MM, LA, ...rest }) => {
        return rest;
      });
      setMatrixDataRows([...formattedMatrixData]);
      setMatrixModelIsOpen(true);
    } catch (err) {}
  };

  const checkVersion = (d) => {
    const unique = (value, index, self) => {
      return self.indexOf(value) === index;
    };
    const allVersionNames = [
      ...d.data.BD,
      ...d.data.KH,
      ...d.data.LK,
      ...d.data.MY,
      ...d.data.PH,
      ...d.data.PK,
      ...d.data.TH,
    ];
    const uniqueVersions = allVersionNames.filter(unique);
    const mappedUniqueVersions = uniqueVersions.map((v) => {
      const versionsAv = d.data[d.column];
      const avail = versionsAv.indexOf(v);
      if (avail > -1) {
        return { version: v, is_selected: true };
      }
      return { version: v, is_selected: false };
    });
    setUniqueVersions([...mappedUniqueVersions]);
    setMatrixVersionData(d);
    setMatrixModelIsOpen(false);
    setOpenVersion(true);
  };

  const handleVersionClick = async (selectVer) => {
  
    if (versionsToBeSaved.length === 0) {
      setVersionsToBeSaved([
        {
          country: matrixVersionData.column,
          template_id: matrixVersionData.data.id,
          selected: selectVer.is_selected,
          versions: selectVer.version,
        },
      ]);
    } else {
      let isExists = false;
      let updatedVersionToBeSaved = versionsToBeSaved.map((version) => {
        if (selectVer.version === version.versions) {
          isExists = true;
          return { ...version, selected: selectVer.is_selected };
        } else return version;
      });
      if (!isExists) {
        updatedVersionToBeSaved.push({
          country: matrixVersionData.column,
          template_id: matrixVersionData.data.id,
          selected: selectVer.is_selected,
          versions: selectVer.version,
        });
      }
      setVersionsToBeSaved([...updatedVersionToBeSaved]);
    }
  };

  const onClickSave = async () => {
    if (versionsToBeSaved.length > 0) {
      for (let i = 0; i < versionsToBeSaved.length; i++) {
        try {
          await addDeleteVersion(versionsToBeSaved[i]);
        } catch (err) {
          toast.error(`Error editing ${matrixVersionData.data.model}`, {
            theme: "colored",
          });
          setVersionsToBeSaved([]);
        }
      }
      toast.success(`module versions updated successfully`, {
        theme: "colored",
      });
      setVersionsToBeSaved([]);
      setReRender(!reRender);
    }
    if (versionsToBeSaved.length === 0) {
      toast.warning(`Select version(s) to save`, {
        theme: "colored",
      });
    }
  };

  return (
    <div>
      <Header />
      <Banner small />
      <PrimeLayout title="Configure Modules">
        <ModelDialog
          open={open}
          setOpen={setOpen}
          addNewTemplate={addNewTemplate}
        />
        <ModelDialog
          edit
          open={openEdit}
          editTemplate={editTemplate}
          setOpen={setOpenEdit}
          dataForEdit={dataForEdit}
          reRender={reRender}
          setReRender={setReRender}
        />
        <DeleteConfirmation
          deleteModule={handleDeleteModel}
          open={openDelete}
          setOpen={setOpenDelete}
          cancelAction={cancelDelete}
        />
        <DialogWrapper
          title={"Select Module Versions"}
          cancelLabel="Cancel"
          actionLabel={"Save"}
          open={openVersion}
          version
          setOpenModal={setOpenVersion}
          acceptButton={() => {
            onClickSave();
            setOpenVersion(false);
          }}
          cancelAction={() => {
            setVersionsToBeSaved([]);
            setOpenVersion(false);
          }}
          onClose={() => {
            setVersionsToBeSaved([]);
            setOpenVersion(false);
          }}
        >
          <SubTitleWrapper>Please select the module version</SubTitleWrapper>
          <VersionWrapper>
            {uniqueVersions.map((ver) => {
              return (
                <VersionPicker
                  handleVersionClick={({ is_selected }) => {
                    handleVersionClick({ ...ver, is_selected });
                  }}
                  versionChecked={ver.is_selected}
                  version={ver.version}
                />
              );
            })}
          </VersionWrapper>
        </DialogWrapper>
        <ModelMatrix
          open={matrixModelIsOpen}
          setOpen={setMatrixModelIsOpen}
          modelMatrixData={matrixDataRows}
          checkVersion={checkVersion}
          handleAddClickModel={handleAddClickModel}
        />
        <Grid
          container
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={3}
        >
          <Grid item xs={12} md={6}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} md={6}>
                <StyledSelect
                  label={"Module Name"}
                  defaultVal={"Select Module Name"}
                  options={modelArray}
                  control={model}
                  setControl={(model) => {
                    setPage(1);
                    setModel(model);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={3}>
            <StyledButton
              iconLeft={<MdLibraryAdd size={20} />}
              variant={"outlined"}
              onClick={handleAddClickModel}
            >
              Add New Module
            </StyledButton>
          </Grid>
          <Grid item xs={12} md={3}>
            <StyledButton
              iconLeft={<IoMdListBox size={20} />}
              variant={"contained"}
              onClick={onClickModelMatrix}
            >
              View Module Matrix
            </StyledButton>
          </Grid>
          <Grid item xs={12}>
            <StyledTable
              headCells={modelHeaders}
              rows={editModelData}
              order={order}
              setOrder={setOrder}
              files={"template"}
              editClick={handleEditModel}
              deleteClick={handleDeleteCheck}
              onFileClick={downloadModel}
              action
              loading={loading}
              deleteEnabaled
              template
            />
            {allModels?.totalPages > 1 && !loading && (
              <Pagination
                count={allModels?.totalPages}
                getPage={getPage}
                page={page}
              />
            )}
          </Grid>
        </Grid>
      </PrimeLayout>
      <Footer />
    </div>
  );
}
